import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Badge,
  Chip,
  Collapse,
  TablePagination,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import EmptyState, { type EmptyStateVariant } from './EmptyState';
import { allTokens } from '@hopdrive/ui/tokens';

export const getElevation = (level: typeof allTokens.elevation.level1) =>
  level.layers.map(l => `${l.offsetX}px ${l.offsetY}px ${l.blur}px ${l.spread}px rgba(0,0,0,${l.opacity})`).join(', ');

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ActiveFilter {
  key: string;
  label: string;
  variant?: 'default' | 'warning';
  /** When true, chip has no delete icon and is not clearable */
  permanent?: boolean;
}

export interface ViewModeOption {
  value: string;
  icon: React.ReactElement;
  label?: string;
}

export interface DataCardProps {
  /** Current search value */
  search: string;
  /** Search change handler */
  onSearchChange: (value: string) => void;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Width of the search field (number = px, or any CSS width string). Defaults to 260. */
  searchWidth?: number | string;

  /** Current view mode key */
  viewMode?: string;
  /** View mode change handler */
  onViewModeChange?: (mode: string) => void;
  /** Available view modes */
  viewModes?: ViewModeOption[];

  /** Filter controls rendered inside the collapsible filter section */
  filterControls?: React.ReactNode;
  /** Controlled filter-panel visibility (omit for internal state) */
  filtersOpen?: boolean;
  /** Called when filter-panel visibility changes (required when filtersOpen is controlled) */
  onFiltersOpenChange?: (open: boolean) => void;
  /** Active filters from all sources (KPI cards, dropdowns, search, etc.) */
  activeFilters?: ActiveFilter[];
  /** Clear a single filter by key */
  onClearFilter?: (key: string) => void;
  /** Clear every active filter */
  onClearAllFilters?: () => void;

  /** Renders a refresh button in the toolbar */
  onRefresh?: () => void;
  /** Spins the refresh icon when true */
  loading?: boolean;

  /** Total row count for pagination */
  totalCount?: number;
  /** Current page (0-indexed) */
  page?: number;
  /** Rows per page */
  rowsPerPage?: number;
  /** Page change handler */
  onPageChange?: (event: unknown, newPage: number) => void;
  /** Rows-per-page change handler */
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Page size options */
  rowsPerPageOptions?: number[];
  /** Show the pagination footer */
  showPagination?: boolean;

  // ── Empty state ─────────────────────────────────────────────────────────

  /** Resource name for empty state (e.g. "Locations") */
  resource?: string;
  /** Icon for the empty state badge */
  emptyIcon?: React.ReactElement;
  /** Whether the underlying dataset has any records before filtering.
   *  `false` → "zero data" variant. `true` / omitted → "no matches" variant. */
  hasData?: boolean;
  /** Empty state semantic variant (passed to EmptyState) */
  emptyVariant?: EmptyStateVariant;
  /** Override the empty-state title (default: "No {resource}") */
  emptyTitle?: string;
  /** Override the empty-state description copy */
  emptyDescription?: string;
  /** Primary CTA label for the empty state (e.g. "Add Location") */
  emptyCtaLabel?: string;
  /** Primary CTA click handler */
  onEmptyCtaClick?: () => void;
  /** Render the primary CTA as a link — pass the Link component */
  emptyCtaComponent?: React.ElementType;
  /** "to" prop when emptyCtaComponent is provided */
  emptyCtaTo?: string;

  /** Optional sx for the Card container (e.g. flex: 1 for fill-height layouts) */
  sx?: React.ComponentProps<typeof Card>['sx'];
  /** When true, content area flexes to fill (for fill-height layouts like settings) */
  fillHeight?: boolean;

  children: React.ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function DataCard({
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  searchWidth = 260,
  viewMode,
  onViewModeChange,
  viewModes,
  filterControls,
  filtersOpen: filtersOpenProp,
  onFiltersOpenChange,
  activeFilters = [],
  onClearFilter,
  onClearAllFilters,
  onRefresh,
  loading = false,
  totalCount = 0,
  page = 0,
  rowsPerPage = 25,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  showPagination = true,
  resource = 'items',
  emptyIcon,
  hasData = true,
  emptyVariant,
  emptyTitle,
  emptyDescription,
  emptyCtaLabel,
  onEmptyCtaClick,
  emptyCtaComponent,
  emptyCtaTo,
  sx,
  fillHeight = false,
  children,
}: DataCardProps) {
  const [filtersOpenInternal, setFiltersOpenInternal] = useState(false);
  const isControlled = filtersOpenProp !== undefined;
  const filtersOpen = isControlled ? filtersOpenProp : filtersOpenInternal;
  const toggleFilters = useCallback(() => {
    const next = !filtersOpen;
    if (isControlled) {
      onFiltersOpenChange?.(next);
    } else {
      setFiltersOpenInternal(next);
    }
  }, [filtersOpen, isControlled, onFiltersOpenChange]);

  // Badge count excludes "search" — search is always visible in the text field
  const filterBadgeCount = activeFilters.filter(f => f.key !== 'search').length;
  const hasFilterControls = !!filterControls;

  // Empty state: show when not loading and zero results
  const showEmptyState = !loading && totalCount === 0;
  const isFilteredEmpty = showEmptyState && hasData;

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        borderRadius: `${allTokens.radius.lg}px`,
        boxShadow: getElevation(allTokens.elevation.level2),
        overflow: 'visible',
        ...sx,
      }}
    >
      {/* ── Toolbar: search (left) | view toggle + filters + refresh (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Search — always renders left */}
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: searchWidth,
            maxWidth: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: `${allTokens.radius.md}px`,
              bgcolor: 'grey.50',
            },
          }}
        />

        {/* Right-side controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* View mode toggle */}
          {viewModes && viewModes.length > 0 && onViewModeChange && (
            <Box
              sx={{
                display: 'flex',
                gap: '2px',
                bgcolor: 'grey.100',
                borderRadius: `${allTokens.radius.md}px`,
                p: '2px',
              }}
            >
              {viewModes.map((mode) => (
                <Tooltip key={mode.value} title={mode.label || mode.value}>
                  <IconButton
                    size="small"
                    onClick={() => onViewModeChange(mode.value)}
                    sx={{
                      borderRadius: `${allTokens.radius.sm}px`,
                      width: 32,
                      height: 32,
                      color: viewMode === mode.value ? 'text.primary' : 'grey.400',
                      bgcolor: viewMode === mode.value ? '#fff' : 'transparent',
                      boxShadow:
                        viewMode === mode.value
                          ? getElevation(allTokens.elevation.level1)
                          : 'none',
                      '&:hover': {
                        bgcolor: viewMode === mode.value ? '#fff' : 'grey.200',
                      },
                    }}
                  >
                    {React.cloneElement(mode.icon as React.ReactElement<any>, {
                      sx: { fontSize: 18 },
                    })}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          )}

          {/* Filter toggle with animated count badge */}
          {hasFilterControls && (
            <Badge
              badgeContent={filterBadgeCount || undefined}
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  fontWeight: 700,
                  fontSize: 11,
                  minWidth: 18,
                  height: 18,
                  transition: 'transform 200ms ease-in-out',
                },
              }}
            >
              <Button
                size="small"
                startIcon={<FilterListIcon />}
                onClick={toggleFilters}
                sx={{
                  textTransform: 'none',
                  borderRadius: `${allTokens.radius.md}px`,
                  bgcolor: filtersOpen ? 'grey.200' : 'grey.100',
                  color: 'grey.700',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                Filters
              </Button>
            </Badge>
          )}

          {/* Refresh — always present when handler provided */}
          {onRefresh && (
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                sx={{ color: 'grey.500' }}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={
                    loading
                      ? {
                          '@keyframes datacard-spin': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' },
                          },
                          animation: 'datacard-spin 1s linear infinite',
                        }
                      : undefined
                  }
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Collapsible filter controls ─────────────────────────────────── */}
      {hasFilterControls && (
        <Collapse in={filtersOpen}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              alignItems: 'center',
            }}
          >
            {filterControls}
          </Box>
        </Collapse>
      )}

      {/* ── Active filter chips ─────────────────────────────────────────── */}
      {activeFilters.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            px: 3,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            alignItems: 'center',
          }}
        >
          {activeFilters.map((f) => {
            const isWarning = f.variant === 'warning';
            return (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                onDelete={onClearFilter && !f.permanent ? () => onClearFilter(f.key) : undefined}
                deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  borderRadius: `${allTokens.radius.sm}px`,
                  bgcolor: isWarning ? 'warning.50' : 'grey.100',
                  color: isWarning ? 'warning.800' : 'grey.700',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: isWarning ? 'warning.400' : 'grey.400',
                    '&:hover': { color: isWarning ? 'warning.600' : 'grey.600' },
                  },
                }}
              />
            );
          })}
          {onClearAllFilters && (
            <Button
              variant="text"
              size="small"
              onClick={onClearAllFilters}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                color: 'grey.600',
                fontWeight: 500,
                minWidth: 'auto',
              }}
            >
              Clear all
            </Button>
          )}
        </Box>
      )}

      {/* ── Content / Empty State ─────────────────────────────────────── */}
      {showEmptyState ? (
        <EmptyState
          bare
          resource={resource}
          icon={emptyIcon}
          variant={emptyVariant ?? (isFilteredEmpty ? 'search' : 'default')}
          title={
            emptyTitle ??
            (isFilteredEmpty ? `No ${resource} match your filters` : undefined)
          }
          description={
            emptyDescription ??
            (isFilteredEmpty ? 'Try adjusting your search or filter criteria.' : undefined)
          }
          // Only offer the "create the first" CTA when there's truly no data.
          // Showing it on a zero-filter-match misleads the user into thinking
          // their data is gone.
          ctaLabel={isFilteredEmpty ? undefined : emptyCtaLabel}
          onCtaClick={isFilteredEmpty ? undefined : onEmptyCtaClick}
          ctaComponent={isFilteredEmpty ? undefined : emptyCtaComponent}
          ctaTo={isFilteredEmpty ? undefined : emptyCtaTo}
          secondaryLabel={
            activeFilters.length > 0 ? 'Clear all filters' : undefined
          }
          onSecondaryClick={
            activeFilters.length > 0 ? onClearAllFilters : undefined
          }
        />
      ) : fillHeight ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      ) : (
        children
      )}

      {/* ── Pagination footer ───────────────────────────────────────────── */}
      {showPagination && totalCount > 0 && onPageChange && onRowsPerPageChange && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                fontSize: 12,
              },
          }}
        />
      )}
    </Card>
  );
}
